import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Tooltip,
  RadioGroup,
  Radio,
  FormControlLabel,
} from '@mui/material'
import {
  Upload,
  Download,
  Visibility,
  Delete,
  Description,
  History,
  Gavel,
  LockOpen,
  CloudUpload,
  MoreVert,
  Star,
  Schedule,
} from '@mui/icons-material'
import { useAppSelector } from '../hooks/redux'
import {
  useGetMemorialsQuery,
  useSubmitMemorialMutation,
  useLazyDownloadMemorialQuery,
  useDeleteMemorialMutation,
  useGetMemorialDeadlineStatusQuery,
  useLazyGetMemorialVersionsQuery,
  useOfficialSubmitMemorialMutation,
  useReleaseMemorialSubmissionLockMutation,
  useForceSubmitMemorialMutation,
  type Memorial,
} from '../store/api/memorialsApi'
import { useGetCasesQuery } from '../store/api/casesApi'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import { useGetRoundsQuery } from '../store/api/roundsApi'
import {
  useListRoleSheetsQuery,
  useUploadRoleSheetMutation,
  useDeleteRoleSheetMutation,
  useLazyDownloadRoleSheetQuery,
} from '../store/api/bracketsApi'
import { hasPermission } from '../utils/permissions'
import { formatCaseDisplay } from '../utils/caseDisplay'
import { PageHeader } from '../components/PageHeader'
import SubmissionDeadlineCountdown from '../components/SubmissionDeadlineCountdown'
import { getFilterPaperSx, getTablePaperSx } from '../theme/sectionSurfaces'
import { gradientBrand } from '../theme/brand'
import { getApiErrorMessage } from '../utils/apiErrorMessage'

export default function MemorialsPage() {
  const { user: currentUser } = useAppSelector((state) => state.auth)
  const [searchParams] = useSearchParams()
  const caseIdFromUrl = searchParams.get('caseId') || ''
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [selectedCaseId, setSelectedCaseId] = useState<string>(caseIdFromUrl)
  // BTC/Admin filters: team, status, round (UAT feedback — chưa tiện rà soát)
  const [filterTeamId, setFilterTeamId] = useState<string>('')
  const [filterStatus, setFilterStatus] = useState<string>('')
  const [filterRoundId, setFilterRoundId] = useState<string>('')

  useEffect(() => {
    if (caseIdFromUrl && caseIdFromUrl !== selectedCaseId) {
      setSelectedCaseId(caseIdFromUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseIdFromUrl])
  
  const { data: cases = [] } = useGetCasesQuery()
  const { data: teams = [], isLoading: isLoadingTeams } = useGetTeamsQuery()
  const { data: rounds = [] } = useGetRoundsQuery()
  const filterableRounds = useMemo(
    () => (selectedCaseId ? rounds.filter((r: any) => r.case_id === selectedCaseId) : rounds),
    [rounds, selectedCaseId],
  )
  const { data: memorials = [], isLoading, refetch } = useGetMemorialsQuery(
    { case_id: selectedCaseId || undefined },
    { skip: false }
  )

  // Group theo (case_id, team_id) → chỉ giữ version mới nhất cho list chính.
  // Lịch sử các version khác xem trong dialog History.
  const latestMemorials = useMemo(() => {
    const map = new Map<string, Memorial>()
    for (const m of memorials) {
      const key = `${m.case_id}::${m.team_id}`
      const existing = map.get(key)
      const v = m.version || 0
      const ev = existing?.version || 0
      if (!existing || v > ev) {
        map.set(key, m)
      }
    }
    let rows = Array.from(map.values())
    if (filterTeamId) rows = rows.filter((m) => m.team_id === filterTeamId)
    if (filterRoundId) rows = rows.filter((m: any) => m.round_id === filterRoundId)
    if (filterStatus === 'official') rows = rows.filter((m: any) => !!m.is_official_submission)
    else if (filterStatus === 'draft') rows = rows.filter((m: any) => !m.submission_locked)
    else if (filterStatus === 'locked') rows = rows.filter((m: any) => !!m.submission_locked)
    return rows
  }, [memorials, filterTeamId, filterRoundId, filterStatus])

  // ── Bảng phân vai (UAT-TC #4 Memorial) ───────────────────────────────
  const {
    data: roleSheets = [],
    refetch: refetchRoleSheets,
  } = useListRoleSheetsQuery(selectedCaseId || '', { skip: !selectedCaseId })
  const [uploadRoleSheet, { isLoading: isUploadingRoleSheet }] = useUploadRoleSheetMutation()
  const [deleteRoleSheet] = useDeleteRoleSheetMutation()
  const [downloadRoleSheet] = useLazyDownloadRoleSheetQuery()

  const [submitMemorial, { isLoading: isSubmitting }] = useSubmitMemorialMutation()
  const [downloadMemorial] = useLazyDownloadMemorialQuery()
  const [deleteMemorial] = useDeleteMemorialMutation()
  const [officialSubmitMemorial, { isLoading: isOfficialSubmitting }] =
    useOfficialSubmitMemorialMutation()
  const [releaseLock, { isLoading: isReleasingLock }] = useReleaseMemorialSubmissionLockMutation()
  const [forceSubmitMemorial, { isLoading: isForceSubmitting }] = useForceSubmitMemorialMutation()
  const [fetchVersions, { data: versionRows = [], isFetching: isVersionsLoading }] =
    useLazyGetMemorialVersionsQuery()
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error' | 'warning' | 'info'
  }>({
    open: false,
    message: '',
    severity: 'success',
  })
  
  // View memorial dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [viewingMemorial, setViewingMemorial] = useState<Memorial | null>(null)

  const [historyDialogOpen, setHistoryDialogOpen] = useState(false)
  const [historyLabel, setHistoryLabel] = useState('')
  const [historyContext, setHistoryContext] = useState<{ case_id: string; team_id: string; locked: boolean } | null>(null)
  const [rowMenuAnchor, setRowMenuAnchor] = useState<{ el: HTMLElement; memorial: Memorial } | null>(null)
  const closeRowMenu = () => setRowMenuAnchor(null)

  const [officialConfirmOpen, setOfficialConfirmOpen] = useState(false)
  const [memorialToOfficial, setMemorialToOfficial] = useState<Memorial | null>(null)

  const [releaseConfirmOpen, setReleaseConfirmOpen] = useState(false)
  const [lockToRelease, setLockToRelease] = useState<{ case_id: string; team_id: string } | null>(
    null
  )

  const [forceSubmitConfirmOpen, setForceSubmitConfirmOpen] = useState(false)
  const [memorialToForceSubmit, setMemorialToForceSubmit] = useState<Memorial | null>(null)
  const [forceSubmitReason, setForceSubmitReason] = useState('')

  const handleForceSubmitClick = (memorial: Memorial) => {
    setMemorialToForceSubmit(memorial)
    setForceSubmitReason('')
    setForceSubmitConfirmOpen(true)
    closeRowMenu()
  }

  const handleForceSubmitConfirm = async () => {
    if (!memorialToForceSubmit) return
    if (!forceSubmitReason.trim()) {
      setSnackbar({ open: true, message: 'Vui lòng nhập lý do ép nộp', severity: 'warning' })
      return
    }
    try {
      await forceSubmitMemorial({ memorialId: memorialToForceSubmit.id, reason: forceSubmitReason }).unwrap()
      setSnackbar({
        open: true,
        message: 'Đã ép nộp bài thi thành công.',
        severity: 'success',
      })
      refetch()
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.data?.detail || error.message || 'Thao tác thất bại',
        severity: 'error',
      })
    } finally {
      setForceSubmitConfirmOpen(false)
      setMemorialToForceSubmit(null)
    }
  }

  // Get user's team
  const userTeam = teams.find((t) => t.members.includes(currentUser?.id || ''))

  // Filter cases assigned to user's team (dùng cho upload form đội)
  const userCases = cases.filter((c) => {
    if (!userTeam) return false
    return c.assigned_team_ids?.includes(userTeam.id)
  })

  const caseForDeadlineBanner = selectedCaseId || ''
  const { data: deadlineStatus } = useGetMemorialDeadlineStatusQuery(caseForDeadlineBanner, {
    skip: !caseForDeadlineBanner,
    pollingInterval: caseForDeadlineBanner ? 1000 : undefined,
  })

  const canSubmitMemorial = currentUser && hasPermission(currentUser.role, 'SUBMIT_MEMORIAL')
  const canAssignCases = currentUser && hasPermission(currentUser.role, 'ASSIGN_CASES')

  // Dropdown lọc theo cuộc thi: BTC/Thư ký thấy tất cả; đội thi chỉ thấy case của đội mình
  const filterableCases = canAssignCases ? cases : userCases
  const canBypassMemorialLock =
    !!currentUser &&
    (hasPermission(currentUser.role, 'ASSIGN_CASES') ||
      hasPermission(currentUser.role, 'MANAGE_USERS'))
  const canViewRoleSheets = !!canAssignCases || (!!canSubmitMemorial && !!userTeam)

  const isOnUserTeam = (teamId: string) =>
    !!userTeam &&
    userTeam.id === teamId &&
    !!currentUser &&
    userTeam.members.includes(currentUser.id)
  // Upload form state (phiên bản do server gán tự động)
  const [uploadForm, setUploadForm] = useState({
    case_id: '',
    team_id: userTeam?.id || '',
    file: null as File | null,
    title: '',
    representing_role: '' as '' | 'plaintiff' | 'defendant',
  })

  const submissionLockedForUpload =
    !!uploadForm.case_id &&
    !!uploadForm.team_id &&
    memorials.some(
      (m) =>
        m.case_id === uploadForm.case_id &&
        m.team_id === uploadForm.team_id &&
        m.submission_locked
    )

  // Set các case_id mà đội user đã khóa nộp — dùng để disable button "Nộp Memorial" ở header
  const userTeamLockedCaseIds = useMemo(() => {
    const set = new Set<string>()
    if (!userTeam) return set
    for (const m of memorials) {
      if (m.team_id === userTeam.id && m.submission_locked && m.case_id) {
        set.add(m.case_id)
      }
    }
    return set
  }, [memorials, userTeam])

  // Disable button khi:
  //  - đội đã khóa case đang được filter (user định upload tiếp cho case này)
  //  - hoặc đội đã khóa TẤT CẢ case của mình
  const allUserCasesLocked =
    userCases.length > 0 && userCases.every((c) => userTeamLockedCaseIds.has(c.id))
  const selectedCaseLockedForUserTeam =
    !!selectedCaseId && userTeamLockedCaseIds.has(selectedCaseId)

  const { data: uploadDeadlineStatus } = useGetMemorialDeadlineStatusQuery(uploadForm.case_id, {
    skip: !uploadDialogOpen || !uploadForm.case_id,
    pollingInterval: uploadDialogOpen && uploadForm.case_id ? 1000 : undefined,
  })

  const handleOpenUploadDialog = () => {
    const preferred =
      (selectedCaseId && userCases.find((c) => c.id === selectedCaseId)?.id) ||
      userCases[0]?.id ||
      ''
    if (preferred) {
      setUploadForm({
        case_id: preferred,
        team_id: userTeam?.id || '',
        file: null,
        title: '',
        representing_role: '',
      })
    }
    setUploadDialogOpen(true)
  }

  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false)
    setUploadForm({
      case_id: '',
      team_id: userTeam?.id || '',
      file: null,
      title: '',
      representing_role: '',
    })
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setUploadForm({ ...uploadForm, file: event.target.files[0] })
    }
  }

  // Create memorial function - matches moduleFunctions.ts
  const handleCreate = async () => {
    if (!uploadForm.file || !uploadForm.case_id || !uploadForm.team_id) {
      setSnackbar({
        open: true,
        message: 'Vui lòng điền đầy đủ thông tin',
        severity: 'error',
      })
      return
    }

    if (uploadDeadlineStatus && !uploadDeadlineStatus.submission_open) {
      setSnackbar({
        open: true,
        message: uploadDeadlineStatus.message || 'Hết hạn nộp đơn khởi kiện.',
        severity: 'error',
      })
      return
    }

    if (submissionLockedForUpload) {
      setSnackbar({
        open: true,
        message: 'Đội đã nộp đơn khởi kiện chính thức — không thể nộp thêm trừ khi BTC mở khóa.',
        severity: 'error',
      })
      return
    }

    if (!uploadForm.representing_role) {
      setSnackbar({
        open: true,
        message: 'Phải chọn vai trò bảo vệ (Nguyên đơn / Bị đơn) trước khi nộp.',
        severity: 'error',
      })
      return
    }

    const formData = new FormData()
    formData.append('file', uploadForm.file)
    formData.append('case_id', uploadForm.case_id)
    formData.append('team_id', uploadForm.team_id)
    if (uploadForm.title) formData.append('title', uploadForm.title)
    formData.append('version', '1')
    formData.append('representing_role', uploadForm.representing_role)

    try {
      await submitMemorial(formData).unwrap()
      handleCloseUploadDialog()
      refetch() // Refetch danh sách memorials
      setSnackbar({
        open: true,
        message: 'Nộp đơn khởi kiện thành công!',
        severity: 'success',
      })
    } catch (error: unknown) {
      setSnackbar({
        open: true,
        message: `Nộp đơn khởi kiện thất bại: ${getApiErrorMessage(error)}`,
        severity: 'error',
      })
    }
  }

  const handleDownload = async (memorialId: string, fileName: string) => {
    try {
      const result = await downloadMemorial(memorialId).unwrap()
      const url = window.URL.createObjectURL(result)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      setSnackbar({
        open: true,
        message: 'Download thành công!',
        severity: 'success',
      })
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Download thất bại: ${error.data?.detail || error.message}`,
        severity: 'error',
      })
    }
  }
  
  // ── UAT-TC #4 Memorial: Upload / Download / Delete Bảng phân vai ────
  const handleUploadRoleSheet = async (file?: File) => {
    if (!file || !selectedCaseId) {
      setSnackbar({
        open: true,
        message: 'Vui lòng chọn cuộc thi và file PDF Bảng phân vai trước.',
        severity: 'warning',
      })
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setSnackbar({ open: true, message: 'File vượt quá 5MB.', severity: 'error' })
      return
    }
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setSnackbar({ open: true, message: 'Bảng phân vai chỉ chấp nhận PDF.', severity: 'error' })
      return
    }
    try {
      await uploadRoleSheet({
        caseId: selectedCaseId,
        title: file.name.replace(/\.pdf$/i, ''),
        file,
      }).unwrap()
      setSnackbar({
        open: true,
        message: `Nộp Bảng phân vai thành công: ${file.name}. BTC đã được thông báo.`,
        severity: 'success',
      })
      refetchRoleSheets()
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: `Nộp Bảng phân vai thất bại: ${err?.data?.detail || err?.message || 'Lỗi không xác định'}`,
        severity: 'error',
      })
    }
  }

  const handleDownloadRoleSheet = async (
    sheetId: string,
    fileName: string | null | undefined,
  ) => {
    try {
      const blob = await downloadRoleSheet(sheetId).unwrap()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName || 'role-sheet.pdf'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: `Tải Bảng phân vai thất bại: ${err?.data?.detail || err?.message || ''}`,
        severity: 'error',
      })
    }
  }

  const handleDeleteRoleSheet = async (sheetId: string) => {
    if (!confirm('Xoá Bảng phân vai này?')) return
    try {
      await deleteRoleSheet(sheetId).unwrap()
      setSnackbar({ open: true, message: 'Đã xoá Bảng phân vai.', severity: 'success' })
      refetchRoleSheets()
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: `Xoá thất bại: ${err?.data?.detail || err?.message || ''}`,
        severity: 'error',
      })
    }
  }

  // View memorial function - matches moduleFunctions.ts
  const handleOpenHistory = (memorial: Memorial) => {
    const c = cases.find((x) => x.id === memorial.case_id)
    setHistoryLabel(c ? formatCaseDisplay(c) : memorial.case_id)
    setHistoryContext({
      case_id: memorial.case_id,
      team_id: memorial.team_id,
      locked: !!memorial.submission_locked,
    })
    setHistoryDialogOpen(true)
    fetchVersions({ case_id: memorial.case_id, team_id: memorial.team_id })
  }

  const handleCloseHistoryDialog = () => {
    setHistoryDialogOpen(false)
    setHistoryLabel('')
    setHistoryContext(null)
  }


  const handleOfficialClick = (memorial: Memorial) => {
    setMemorialToOfficial(memorial)
    setOfficialConfirmOpen(true)
  }

  const handleOfficialConfirm = async () => {
    if (!memorialToOfficial) return
    try {
      await officialSubmitMemorial(memorialToOfficial.id).unwrap()
      setSnackbar({
        open: true,
        message: 'Đã xác nhận nộp đơn khởi kiện chính thức. Đội không thể nộp thêm hoặc xóa trừ khi BTC mở khóa.',
        severity: 'success',
      })
      refetch()
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.data?.detail || error.message || 'Thao tác thất bại',
        severity: 'error',
      })
    } finally {
      setOfficialConfirmOpen(false)
      setMemorialToOfficial(null)
    }
  }

  const handleReleaseLockClick = (case_id: string, team_id: string) => {
    setLockToRelease({ case_id, team_id })
    setReleaseConfirmOpen(true)
  }

  const handleReleaseLockConfirm = async () => {
    if (!lockToRelease) return
    try {
      await releaseLock({ case_id: lockToRelease.case_id, team_id: lockToRelease.team_id }).unwrap()
      setSnackbar({ open: true, message: 'Đã mở khóa nộp đơn khởi kiện cho đội.', severity: 'success' })
      refetch()
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.data?.detail || error.message || 'Mở khóa thất bại',
        severity: 'error',
      })
    } finally {
      setReleaseConfirmOpen(false)
      setLockToRelease(null)
    }
  }

  const handleView = (memorial: Memorial) => {
    setViewingMemorial(memorial)
    setViewDialogOpen(true)
  }
  
  const handleCloseViewDialog = () => {
    setViewDialogOpen(false)
    setViewingMemorial(null)
  }

  // Delete memorial handler
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [memorialToDelete, setMemorialToDelete] = useState<Memorial | null>(null)

  const handleDeleteClick = (memorial: Memorial) => {
    setMemorialToDelete(memorial)
    setDeleteConfirmOpen(true)
  }

  // Delete memorial function - matches moduleFunctions.ts
  const handleDelete = async () => {
    if (!memorialToDelete) return

    try {
      await deleteMemorial(memorialToDelete.id).unwrap()
      setSnackbar({
        open: true,
        message: 'Xóa đơn khởi kiện thành công!',
        severity: 'success',
      })
      refetch()
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Xóa đơn khởi kiện thất bại: ${error.data?.detail || error.message}`,
        severity: 'error',
      })
    } finally {
      setDeleteConfirmOpen(false)
      setMemorialToDelete(null)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false)
    setMemorialToDelete(null)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'submitted':
        return 'success'
      case 'draft':
        return 'default'
      default:
        return 'info'
    }
  }

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      <PageHeader
        title="Đơn khởi kiện"
        subtitle="Nộp đơn khởi kiện, theo dõi hạn chót và lịch sử phiên bản theo từng cuộc thi."
        icon={<Description />}
        actions={
          canSubmitMemorial && userTeam ? (
            (() => {
              const isDeadlineClosed =
                !!deadlineStatus && !deadlineStatus.submission_open && !!caseForDeadlineBanner
              const lockedDisable = selectedCaseLockedForUserTeam || allUserCasesLocked
              const disabled = userCases.length === 0 || isDeadlineClosed || lockedDisable
              const tooltipText = !disabled
                ? ''
                : userCases.length === 0
                  ? 'Đội bạn chưa được phân cuộc thi nào'
                  : isDeadlineClosed
                    ? 'Đã hết hạn nộp đơn khởi kiện cho cuộc thi đang chọn'
                    : selectedCaseLockedForUserTeam
                      ? 'Đội đã nộp chính thức cho cuộc thi này — liên hệ BTC để mở khóa'
                      : 'Đội đã nộp chính thức cho tất cả cuộc thi được phân'
              return (
                <Tooltip title={tooltipText} arrow disableHoverListener={!disabled}>
                  <span>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<Upload />}
                      onClick={handleOpenUploadDialog}
                      disabled={disabled}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        background: gradientBrand,
                        '&:hover': { background: gradientBrand, filter: 'brightness(1.06)' },
                      }}
                    >
                      Nộp Đơn khởi kiện
                    </Button>
                  </span>
                </Tooltip>
              )
            })()
          ) : null
        }
      />

      {!canSubmitMemorial && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Chỉ sinh viên (students) mới có quyền nộp đơn khởi kiện cho cuộc thi được phân cho đội của mình.
        </Alert>
      )}

      {canSubmitMemorial && userCases.length === 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Bạn chưa có case nào được assign cho đội của mình.
        </Alert>
      )}

      {/* Filter */}
      <Paper
        elevation={0}
        sx={(theme) => ({
          ...getFilterPaperSx(theme),
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        })}
      >
        <FormControl fullWidth size="small">
          <InputLabel>Cuộc thi</InputLabel>
          <Select
            value={selectedCaseId}
            label="Cuộc thi"
            onChange={(e) => setSelectedCaseId(e.target.value)}
          >
            <MenuItem value="">Tất cả</MenuItem>
            {filterableCases.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {formatCaseDisplay(c)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {canAssignCases && (
          <>
            <FormControl fullWidth size="small">
              <InputLabel>Đội thi</InputLabel>
              <Select
                value={filterTeamId}
                label="Đội thi"
                onChange={(e) => setFilterTeamId(e.target.value)}
              >
                <MenuItem value="">Tất cả đội</MenuItem>
                {teams.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Trạng thái</InputLabel>
              <Select
                value={filterStatus}
                label="Trạng thái"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="">Tất cả</MenuItem>
                <MenuItem value="official">Bản chính thức</MenuItem>
                <MenuItem value="draft">Bản nháp / chưa lock</MenuItem>
                <MenuItem value="locked">Đã khóa nộp</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Vòng</InputLabel>
              <Select
                value={filterRoundId}
                label="Vòng"
                onChange={(e) => setFilterRoundId(e.target.value)}
              >
                <MenuItem value="">Tất cả vòng</MenuItem>
                {filterableRounds.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
      </Paper>


      {/* Khi quá hạn — Alert lớn vì critical */}
      {caseForDeadlineBanner && deadlineStatus && !deadlineStatus.submission_open && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Đã quá ngày kết thúc cuộc thi
          </Typography>
          <Typography variant="body2">{deadlineStatus.message}</Typography>
          <SubmissionDeadlineCountdown status={deadlineStatus} />
        </Alert>
      )}

      {/* Khi đang trong hạn — Chip nhỏ ở góc, hover để xem chi tiết (đồng bộ với Chứng cứ) */}
      {deadlineStatus && deadlineStatus.submission_open && caseForDeadlineBanner && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Tooltip
            arrow
            placement="left"
            title={
              <Box sx={{ p: 0.5 }}>
                <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                  {deadlineStatus.message}
                </Typography>
                <SubmissionDeadlineCountdown status={deadlineStatus} />
              </Box>
            }
          >
            <Chip
              size="small"
              color="success"
              variant="outlined"
              icon={<Schedule fontSize="small" />}
              label="Đang mở nhận đơn khởi kiện"
              sx={{ cursor: 'help' }}
            />
          </Tooltip>
        </Box>
      )}

      {userTeam &&
        memorials.some(
          (m) => m.team_id === userTeam.id && m.submission_locked
        ) && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Đội đã nộp đơn khởi kiện chính thức — không thể nộp bản mới hoặc xóa. Nếu cần chỉnh sửa ngoại lệ,
            liên hệ BTC để mở khóa.
          </Alert>
        )}

      {/* Memorials Table */}
      <Paper elevation={0} sx={(theme) => getTablePaperSx(theme)}>
        <TableContainer>
          <Table sx={{ minWidth: 1120 }}>
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: 'background.default', whiteSpace: 'nowrap' } }}>
                <TableCell sx={{ minWidth: 220 }}>Cuộc thi</TableCell>
                <TableCell sx={{ minWidth: 160 }}>Đội thi</TableCell>
                <TableCell sx={{ minWidth: 220 }}>File</TableCell>
                <TableCell sx={{ minWidth: 120 }}>Kích thước</TableCell>
                <TableCell sx={{ minWidth: 110 }}>Version</TableCell>
                <TableCell sx={{ minWidth: 190 }}>Trạng thái</TableCell>
                <TableCell sx={{ minWidth: 140 }}>Nộp chính thức</TableCell>
                <TableCell sx={{ minWidth: 160 }}>Ngày nộp</TableCell>
                <TableCell align="right" sx={{ minWidth: 160 }}>Thao tác</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading || isLoadingTeams ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : latestMemorials.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Chưa có đơn khởi kiện nào
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                latestMemorials.map((memorial) => {
                  const caseItem = cases.find((c) => c.id === memorial.case_id)
                  const team = teams.find((t) => t.id === memorial.team_id)
                  const teamName = team ? team.name : memorial.team_id
                  const deleteDisabled = !!memorial.submission_locked && !canBypassMemorialLock
                  // Đếm số version để hiển thị badge cạnh icon History
                  const versionCount = memorials.filter(
                    (m) => m.case_id === memorial.case_id && m.team_id === memorial.team_id,
                  ).length
                  return (
                    <TableRow key={memorial.id}>
                      <TableCell>{caseItem ? formatCaseDisplay(caseItem) : memorial.case_id}</TableCell>
                      <TableCell>{teamName}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Description />
                          <Typography variant="body2">{memorial.file_name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{(memorial.file_size / 1024 / 1024).toFixed(2)} MB</TableCell>
                      <TableCell>
                        <Tooltip
                          title={
                            versionCount > 1
                              ? `Click để xem ${versionCount} phiên bản của đội này`
                              : 'Chỉ có 1 phiên bản'
                          }
                        >
                          <Chip
                            size="small"
                            label={`v${memorial.version || 1}${versionCount > 1 ? ` / ${versionCount}` : ''}`}
                            variant="outlined"
                            color={versionCount > 1 ? 'primary' : 'default'}
                            onClick={
                              versionCount > 1
                                ? () => handleOpenHistory(memorial)
                                : undefined
                            }
                            sx={{ cursor: versionCount > 1 ? 'pointer' : 'default' }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {memorial.representing_role === 'plaintiff' && (
                            <Chip
                              size="small"
                              label="Bảo vệ Nguyên đơn"
                              color="info"
                              variant="filled"
                            />
                          )}
                          {memorial.representing_role === 'defendant' && (
                            <Chip
                              size="small"
                              label="Bảo vệ Bị đơn"
                              color="secondary"
                              variant="filled"
                            />
                          )}
                          <Chip
                            label={memorial.status}
                            color={getStatusColor(memorial.status)}
                            size="small"
                          />
                          {memorial.submission_locked && (
                            <Chip label="Đã khóa đội" color="warning" size="small" variant="outlined" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {memorial.is_official_submission ? (
                          <Chip label="Bản được chọn" size="small" color="success" variant="outlined" />
                        ) : memorial.submission_locked ? (
                          <Typography variant="caption" color="text.secondary">
                            Bản lưu
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {memorial.submitted_at
                          ? new Date(memorial.submitted_at).toLocaleString('vi-VN')
                          : '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <IconButton
                          size="small"
                          onClick={() => handleDownload(memorial.id, memorial.file_name)}
                          title="Tải về bản mới nhất"
                        >
                          <Download fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          title={`Lịch sử phiên bản (${versionCount} bản)`}
                          onClick={() => handleOpenHistory(memorial)}
                        >
                          <History fontSize="small" />
                        </IconButton>
                        {isOnUserTeam(memorial.team_id) && !memorial.submission_locked && (
                          <IconButton
                            size="small"
                            color="primary"
                            title="Nộp đơn khởi kiện chính thức (khóa sửa/xóa đội)"
                            onClick={() => handleOfficialClick(memorial)}
                          >
                            <Gavel fontSize="small" />
                          </IconButton>
                        )}
                        {/* BTC/Admin: nút Force Submit + Force Unlock hiển thị trực
                            tiếp để không phải mò trong menu 3-chấm (UAT feedback). */}
                        {canBypassMemorialLock && !memorial.submission_locked && (
                          <IconButton
                            size="small"
                            color="secondary"
                            title="Ép nộp bài (Force Submit) — chốt bản này làm bản chính thức"
                            onClick={() => handleForceSubmitClick(memorial)}
                            sx={{ border: '1px dashed', borderColor: 'secondary.main', borderRadius: 1 }}
                          >
                            <Gavel fontSize="small" />
                          </IconButton>
                        )}
                        {canAssignCases && memorial.submission_locked && (
                          <IconButton
                            size="small"
                            color="warning"
                            title="Mở khóa nộp bài (BTC) — cho phép đội nộp lại"
                            onClick={() => handleReleaseLockClick(memorial.case_id, memorial.team_id)}
                            sx={{ border: '1px dashed', borderColor: 'warning.main', borderRadius: 1 }}
                          >
                            <LockOpen fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          title="Thao tác khác"
                          onClick={(e) => setRowMenuAnchor({ el: e.currentTarget, memorial })}
                        >
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* UAT-TC #4 Memorial: Bảng phân vai (Role Assignment Sheet) */}
      {selectedCaseId && canViewRoleSheets && (
        <Paper elevation={0} sx={(theme) => ({ ...getFilterPaperSx(theme), mt: 3 })}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { sm: 'center' }, gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Bảng phân vai
              </Typography>
              <Typography variant="caption" color="text.secondary">
                File PDF phân công vai trò luật sư / nguyên đơn / bị đơn cho đội. Tối đa 5MB.
              </Typography>
            </Box>
            {canSubmitMemorial && userTeam && (
              <Button
                component="label"
                variant="contained"
                startIcon={<CloudUpload />}
                disabled={isUploadingRoleSheet}
                sx={{ background: gradientBrand, textTransform: 'none', alignSelf: { xs: 'stretch', sm: 'center' } }}
              >
                {isUploadingRoleSheet ? 'Đang nộp...' : 'Nộp Bảng phân vai'}
                <input
                  hidden
                  type="file"
                  accept=".pdf"
                  onChange={(e) => handleUploadRoleSheet(e.target.files?.[0])}
                />
              </Button>
            )}
          </Box>
          {roleSheets.length === 0 ? (
            <Alert severity="info">
              Chưa có Bảng phân vai cho cuộc thi này.
              {canSubmitMemorial && userTeam && ' Đội bạn có thể nộp bằng nút bên trên.'}
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 220 }}><strong>Tên file</strong></TableCell>
                    <TableCell sx={{ minWidth: 140 }}><strong>Đội nộp</strong></TableCell>
                    <TableCell sx={{ minWidth: 100 }}><strong>Vai trò</strong></TableCell>
                    <TableCell sx={{ minWidth: 100 }}><strong>Kích thước</strong></TableCell>
                    <TableCell sx={{ minWidth: 130 }}><strong>Ngày nộp</strong></TableCell>
                    <TableCell sx={{ minWidth: 110 }}><strong>Trạng thái</strong></TableCell>
                    <TableCell align="right" sx={{ minWidth: 130 }}><strong>Thao tác</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roleSheets.map((s) => {
                    const team = teams.find((t) => t.id === s.team_id)
                    const isOwn = userTeam && s.team_id === userTeam.id
                    return (
                      <TableRow key={s.id} hover>
                        <TableCell>{s.title || s.file_name}</TableCell>
                        <TableCell>{team?.name || (s.team_id ? s.team_id : '— BTC —')}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={s.uploader_role || (s.team_id ? 'team' : 'btc')}
                            color={s.team_id ? 'primary' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {s.file_size != null ? `${(s.file_size / 1024).toFixed(1)} KB` : '—'}
                        </TableCell>
                        <TableCell>
                          {s.uploaded_at
                            ? new Date(s.uploaded_at).toLocaleString('vi-VN')
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={s.team_id ? 'Đã nộp Bảng phân vai' : 'BTC ban hành'}
                            color={s.team_id ? 'success' : 'info'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => s.id && handleDownloadRoleSheet(s.id, s.file_name)}
                            title="Tải xuống"
                          >
                            <CloudUpload sx={{ transform: 'rotate(180deg)' }} fontSize="small" />
                          </IconButton>
                          {isOwn && s.id && (
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteRoleSheet(s.id!)}
                              color="error"
                              title="Xoá"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Upload Dialog */}
      <Dialog 
        open={uploadDialogOpen} 
        onClose={handleCloseUploadDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 1, sm: 2 },
            m: { xs: 1, sm: 2 },
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, fontWeight: 600 }}>
          Nộp Memorial
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: { xs: 1, sm: 2 } }}>
            <FormControl fullWidth required>
              <InputLabel>Cuộc thi *</InputLabel>
              <Select
                value={uploadForm.case_id}
                label="Cuộc thi *"
                onChange={(e) =>
                  setUploadForm({ ...uploadForm, case_id: e.target.value, representing_role: '' })
                }
                required
              >
                {userCases.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {formatCaseDisplay(c)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* BR Vòng Sơ loại 9.2: chọn vai trò bảo vệ */}
            {uploadForm.case_id &&
              (() => {
                const selectedCase = userCases.find((c) => c.id === uploadForm.case_id) as any
                const plaintiffName = selectedCase?.plaintiff || 'Nguyên đơn'
                const defendantName = selectedCase?.defendant || 'Bị đơn'
                return (
                  <FormControl required>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      Vai trò bảo vệ *
                    </Typography>
                    <RadioGroup
                      value={uploadForm.representing_role}
                      onChange={(e) =>
                        setUploadForm({
                          ...uploadForm,
                          representing_role: e.target.value as 'plaintiff' | 'defendant',
                        })
                      }
                    >
                      <FormControlLabel
                        value="plaintiff"
                        control={<Radio size="small" />}
                        label={`Bảo vệ Nguyên đơn — ${plaintiffName}`}
                      />
                      <FormControlLabel
                        value="defendant"
                        control={<Radio size="small" />}
                        label={`Bảo vệ Bị đơn — ${defendantName}`}
                      />
                    </RadioGroup>
                    <Typography variant="caption" color="text.secondary">
                      Quan trọng: chấm điểm tiêu chí 5.3 phụ thuộc vào vai trò này.
                    </Typography>
                  </FormControl>
                )
              })()}

            <TextField
              fullWidth
              label="Tiêu đề (tùy chọn)"
              value={uploadForm.title}
              onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
            />
            <Typography variant="body2" color="text.secondary">
              Số phiên bản do hệ thống tự tăng mỗi lần nộp file (lịch sử xem tại nút biểu tượng lịch sử).
            </Typography>
            <Button variant="outlined" component="label" fullWidth startIcon={<Upload />}>
              Chọn File (PDF hoặc DOCX)
              <input type="file" hidden accept=".pdf,.docx" onChange={handleFileChange} />
            </Button>
            {uploadForm.file && (
              <Typography variant="body2">
                Đã chọn: {uploadForm.file.name} ({(uploadForm.file.size / 1024 / 1024).toFixed(2)} MB)
              </Typography>
            )}
            {submissionLockedForUpload && (
              <Alert severity="warning">
                Đội đã nộp đơn khởi kiện chính thức — không thể nộp thêm cho đến khi BTC mở khóa.
              </Alert>
            )}
            {/* Chỉ hiện cảnh báo quá hạn — banner ngoài đã có thông tin countdown nếu còn hạn */}
            {uploadDeadlineStatus && !uploadDeadlineStatus.submission_open && (
              <Alert severity="error" variant="outlined">
                Đã quá ngày kết thúc cuộc thi. {uploadDeadlineStatus.message}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, gap: 1 }}>
          <Button 
            onClick={handleCloseUploadDialog}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Hủy
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={
              isSubmitting ||
              submissionLockedForUpload ||
              !!(uploadDeadlineStatus && !uploadDeadlineStatus.submission_open) ||
              !uploadForm.representing_role ||
              !uploadForm.file
            }
            sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 1 }}
          >
            {isSubmitting ? <CircularProgress size={20} /> : 'Nộp Đơn khởi kiện'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Memorial Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={handleCloseViewDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 1, sm: 2 },
            m: { xs: 1, sm: 2 },
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, fontWeight: 600 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Description />
            <Typography variant="h6">Chi tiết Đơn khởi kiện</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          {viewingMemorial && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Tiêu đề
                </Typography>
                <Typography variant="body1">{viewingMemorial.title || viewingMemorial.file_name}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Cuộc thi
                </Typography>
                <Typography variant="body1">
                  {cases.find((c) => c.id === viewingMemorial.case_id) 
                    ? formatCaseDisplay(cases.find((c) => c.id === viewingMemorial.case_id)!)
                    : viewingMemorial.case_id}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Đội thi
                </Typography>
                <Typography variant="body1">
                  {(() => {
                    const team = teams.find((t) => t.id === viewingMemorial.team_id)
                    return team ? team.name : (isLoadingTeams ? 'Đang tải...' : viewingMemorial.team_id)
                  })()}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  File
                </Typography>
                <Typography variant="body1">{viewingMemorial.file_name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Kích thước: {(viewingMemorial.file_size / 1024 / 1024).toFixed(2)} MB
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Version
                </Typography>
                <Chip label={viewingMemorial.version || 1} size="small" />
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Trạng thái
                </Typography>
                <Chip
                  label={viewingMemorial.status}
                  color={getStatusColor(viewingMemorial.status)}
                  size="small"
                />
              </Box>
              {viewingMemorial.submitted_at && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Ngày nộp
                  </Typography>
                  <Typography variant="body1">
                    {new Date(viewingMemorial.submitted_at).toLocaleString('vi-VN')}
                  </Typography>
                </Box>
              )}
              {viewingMemorial.tags && viewingMemorial.tags.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Tags
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {(viewingMemorial.tags ?? [])
                      .filter((tag: string) => {
                        const tagList = viewingMemorial.tags ?? []
                        // Bỏ qua team_id và case_id nếu đã có team: và case:
                        if (tag.startsWith('team_id:') && tagList.some((t: string) => t.startsWith('team:'))) {
                          return false
                        }
                        if (tag.startsWith('case_id:') && tagList.some((t: string) => t.startsWith('case:'))) {
                          return false
                        }
                        return true
                      })
                      .map((tag: string, idx: number) => {
                        // Format tag để hiển thị tên thay vì ID
                        let displayTag = tag
                        if (tag.startsWith('team:')) {
                          displayTag = tag.replace('team:', 'Đội: ')
                        } else if (tag.startsWith('case:')) {
                          displayTag = tag.replace('case:', 'Cuộc thi: ')
                        } else if (tag.startsWith('case_id:')) {
                          const caseId = tag.replace('case_id:', '')
                          const caseItem = cases.find((c) => c.id === caseId)
                          displayTag = caseItem ? `Cuộc thi: ${formatCaseDisplay(caseItem)}` : tag
                        } else if (tag.startsWith('team_id:')) {
                          const teamId = tag.replace('team_id:', '')
                          const team = teams.find((t) => t.id === teamId)
                          displayTag = team ? `Đội: ${team.name}` : tag
                        } else if (tag.startsWith('case_number:')) {
                          displayTag = tag.replace('case_number:', 'Số vụ án: ')
                        }
                        return (
                          <Chip key={idx} label={displayTag} size="small" variant="outlined" />
                        )
                      })}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {viewingMemorial && (
            <Button
              startIcon={<Download />}
              onClick={() => handleDownload(viewingMemorial.id, viewingMemorial.file_name)}
            >
              Download
            </Button>
          )}
          <Button onClick={handleCloseViewDialog}>Đóng</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historyDialogOpen} onClose={handleCloseHistoryDialog} maxWidth="md" fullWidth>
        <DialogTitle>Lịch sử phiên bản đơn khởi kiện</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {historyLabel}
          </Typography>
          {isVersionsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : versionRows.length === 0 ? (
            <Typography variant="body2">Chưa có bản ghi phiên bản.</Typography>
          ) : (
            <TableContainer sx={(theme) => getTablePaperSx(theme)}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ver.</TableCell>
                    <TableCell>File</TableCell>
                    <TableCell>Ngày nộp</TableCell>
                    <TableCell>Trạng thái</TableCell>
                    <TableCell align="right">Thao tác</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {versionRows.map((row) => (
                    <TableRow key={row.memorial_id}>
                      <TableCell>v{row.version}</TableCell>
                      <TableCell>{row.file_name}</TableCell>
                      <TableCell>
                        {row.submitted_at
                          ? new Date(row.submitted_at).toLocaleString('vi-VN')
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {row.is_official_submission ? (
                          <Chip size="small" label="Bản chính thức" color="success" variant="outlined" icon={<Star fontSize="small" />} />
                        ) : (
                          <Typography variant="caption" color="text.secondary">Bản lưu</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <IconButton
                          size="small"
                          title="Tải về bản này"
                          onClick={() => handleDownload(row.memorial_id, row.file_name)}
                        >
                          <Download fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {historyContext?.locked && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Đội đã nộp bản chính thức. Chỉ Ban tổ chức mới có thể đổi bản chính thức bằng cách mở khóa trước.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHistoryDialog}>Đóng</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={officialConfirmOpen} onClose={() => setOfficialConfirmOpen(false)}>
        <DialogTitle>Xác nhận nộp đơn khởi kiện chính thức</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Sau khi xác nhận, đội không thể nộp thêm bản đơn khởi kiện mới hoặc xóa bản đã nộp, trừ khi Ban tổ
            chức mở khóa ngoại lệ. Bạn chắc chắn muốn chọn bản này là bản chính thức?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOfficialConfirmOpen(false)}>Hủy</Button>
          <Button variant="contained" onClick={handleOfficialConfirm} disabled={isOfficialSubmitting}>
            {isOfficialSubmitting ? <CircularProgress size={20} /> : 'Xác nhận'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={releaseConfirmOpen} onClose={() => setReleaseConfirmOpen(false)}>
        <DialogTitle>Mở khóa nộp đơn khởi kiện (BTC)</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Đội sẽ được phép nộp bản đơn khởi kiện mới và xóa bản cũ trở lại. Tiếp tục?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReleaseConfirmOpen(false)}>Hủy</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleReleaseLockConfirm}
            disabled={isReleasingLock}
          >
            {isReleasingLock ? <CircularProgress size={20} /> : 'Mở khóa'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={forceSubmitConfirmOpen} onClose={() => setForceSubmitConfirmOpen(false)}>
        <DialogTitle>Ép nộp bài thi (Force Submit) - Dành cho BTC</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Sử dụng trong trường hợp khẩn cấp khi đội thi gặp sự cố. Hệ thống sẽ bỏ qua deadline và khóa bài này thành bản chính thức.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Lý do ép nộp"
            value={forceSubmitReason}
            onChange={(e) => setForceSubmitReason(e.target.value)}
            placeholder="Ví dụ: Lỗi mạng phút chót"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForceSubmitConfirmOpen(false)}>Hủy</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleForceSubmitConfirm}
            disabled={isForceSubmitting || !forceSubmitReason.trim()}
          >
            {isForceSubmitting ? <CircularProgress size={20} /> : 'Ép nộp'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog 
        open={deleteConfirmOpen} 
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            borderRadius: { xs: 1, sm: 2 },
            m: { xs: 1, sm: 2 },
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, fontWeight: 600 }}>
          Xác nhận xóa
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <Typography>
            Bạn có chắc muốn xóa memorial "{memorialToDelete?.title || memorialToDelete?.file_name}"?
            Hành động này không thể hoàn tác.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, gap: 1 }}>
          <Button 
            onClick={handleDeleteCancel}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Hủy
          </Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 1 }}
          >
            Xóa
          </Button>
        </DialogActions>
      </Dialog>

      {/* Row action menu (3-dots) — gom các action ít dùng để cột Thao tác gọn hơn */}
      <Menu
        anchorEl={rowMenuAnchor?.el}
        open={!!rowMenuAnchor}
        onClose={closeRowMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {rowMenuAnchor && (() => {
          const m = rowMenuAnchor.memorial
          const canUnlock = !!canAssignCases && !!m.submission_locked
          const deleteDisabled = !!m.submission_locked && !canBypassMemorialLock
          return (
            <Box>
              <MenuItem
                onClick={() => {
                  handleView(m)
                  closeRowMenu()
                }}
              >
                <ListItemIcon><Visibility fontSize="small" /></ListItemIcon>
                <ListItemText>Xem chi tiết</ListItemText>
              </MenuItem>
              {canUnlock && (
                <MenuItem
                  onClick={() => {
                    handleReleaseLockClick(m.case_id, m.team_id)
                    closeRowMenu()
                  }}
                >
                  <ListItemIcon><LockOpen fontSize="small" color="secondary" /></ListItemIcon>
                  <ListItemText>Mở khóa nộp bài (BTC)</ListItemText>
                </MenuItem>
              )}
              {canBypassMemorialLock && (
                <MenuItem
                  onClick={() => {
                    handleForceSubmitClick(m)
                  }}
                >
                  <ListItemIcon><Gavel fontSize="small" color="secondary" /></ListItemIcon>
                  <ListItemText>Ép nộp bài thi (Force Submit)</ListItemText>
                </MenuItem>
              )}
              <MenuItem
                onClick={() => {
                  handleDeleteClick(m)
                  closeRowMenu()
                }}
                disabled={deleteDisabled}
              >
                <ListItemIcon><Delete fontSize="small" color={deleteDisabled ? 'disabled' : 'error'} /></ListItemIcon>
                <ListItemText>{deleteDisabled ? 'Đã khóa — chỉ BTC xóa được' : 'Xóa đơn khởi kiện'}</ListItemText>
              </MenuItem>
            </Box>
          )
        })()}
      </Menu>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
